<template>
  <component :is="!context.isMobile ? DropdownMenuItem : DrawerClose" as-child>
    <button
      ref="buttonRef"
      type="button"
      :disabled="props.disabled"
      :class="[
        'group/item',
        'relative flex shrink-0 cursor-pointer select-none items-center gap-3 self-stretch rounded-md border-none px-3 transition-colors duration-75',
        'focus-visible:outline outline-2 outline-gray-100',
        'text-left text-sm',
        // 2025-02-15: Added to allow truncating labels of very long subjects in the related emails menu.
        'max-w-full',
        !context.isMobile && 'h-7',
        context.isMobile && 'h-9',
        props.premium && !props.disabled && 'opacity-50',
        props.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        defaultedVariant === 'default' && 'hover:bg-gray-700',
        defaultedVariant === 'accent' && 'bg-blue-500 hover:bg-blue-600',
        defaultedVariant === 'danger' && 'hover:bg-red-600',
        // TODO: would be nice if we group the rounding of corners when multiple items are selected!
        // Sadly, we cannot use `first-of-type` et al because they don't support data attributes.
      ]"
    >
      <slot v-if="slots.icon" name="icon" />
      <component
        :is="iconMappingMicro[props.pending ? 'arrow-path' : props.icon]"
        v-else-if="props.icon"
        :class="[
          'size-4 shrink-0 transition-colors',
          defaultedVariant === 'default' && 'text-gray-400',
          defaultedVariant === 'accent' && 'text-white',
          defaultedVariant === 'danger' &&
            'text-gray-400 group-hover/item:text-white',
        ]"
      />

      <span
        :class="[
          'min-w-0 grow break-words',
          // 2025-02-15: Added to allow truncating labels of very long subjects in the related emails menu.
          'truncate',
        ]"
      >
        {{ props.label }}
      </span>

      <BoltIcon
        v-if="premium"
        :class="[
          'size-4 shrink-0 transition-colors',
          defaultedVariant === 'default' && 'text-gray-400',
          defaultedVariant === 'accent' && 'text-white',
          defaultedVariant === 'danger' &&
            'text-gray-400 group-hover/item:text-white',
        ]"
      />
      <div
        v-if="props.hotkey"
        class="hidden rounded bg-gray-700 px-1.5 text-center font-mono text-[9px] uppercase lg:block"
      >
        {{ props.hotkey }}
      </div>

      <DropIndicator :edge="draggable.edge" />
    </button>
  </component>
</template>

<script setup lang="ts">
import { DropdownMenuItem } from "radix-vue";
import { DrawerClose } from "vaul-vue";
import { ref, toRef } from "vue";

import BoltIcon from "@/icons/heroicons/bolt-micro.svg";
import { iconMappingMicro, MappedIconMicro } from "@/icons/icon-mapping-micro";
import { useReorderableListItem } from "@/lib/pragmatic-dnd";
import DropIndicator from "@/lib/pragmatic-dnd/DropIndicator.vue";

import { inject } from "./context";

export type Variant = "default" | "accent" | "danger";

const context = inject();

const props = defineProps<{
  label: string;
  icon?: MappedIconMicro;
  /** Convenient property for setting variant="accent", ignored if variant prop is present  */
  active?: boolean;
  premium?: boolean;
  disabled?: boolean;
  pending?: boolean;
  variant?: Variant;
  hotkey?: string;
  index?: number;
}>();

const slots = defineSlots<{
  icon?(): unknown;
}>();

const buttonRef = ref<HTMLButtonElement>();

const draggable = useReorderableListItem({
  element: buttonRef,
  index: () => props.index ?? -1,
});

const defaultedVariant = toRef(() => {
  return props.variant ?? (props.active ? "accent" : "default");
});
</script>
